/**
 * Stage 12 — keep the Territory (Grid) section exactly aligned to the top of
 * the visible viewport on mobile Safari, so its bottom toolbar (LIVE /
 * REVEAL AREAS / MENU) is never pushed below the screen edge.
 *
 * Safari has no CSS scroll anchoring: when its dynamic bars resize the
 * viewport, the 100dvh-based sections ABOVE the Grid change height and push it
 * down; a smooth scroll can also end on a stale target if layout above settles
 * late. Both are corrected here, and only for the Grid. Any user input
 * (touch / wheel / key) cancels a pending correction, so manual scrolling —
 * including the Stage 10 "scroll up to reveal the topbar" — is never fought.
 */

const ALIGNED_PX = 2;
const SETTLE_CHECK_MS = 350;
const SETTLE_CHECKS = 8;

/** Touch devices only: desktop keeps its exact previous behaviour. */
export const isTouchViewport = (): boolean => window.matchMedia("(pointer: coarse)").matches;

function gridTop(): number | null {
  const el = document.getElementById("grid");
  return el ? el.getBoundingClientRect().top : null;
}

function snapGrid(): void {
  const top = gridTop();
  if (top !== null && Math.abs(top) > ALIGNED_PX) {
    window.scrollTo({ top: window.scrollY + top, behavior: "auto" });
  }
}

/** Smooth-scroll to the Grid, then re-align once scrolling and layout settle. */
export function scrollToGridAligned(): void {
  document.getElementById("grid")?.scrollIntoView({ behavior: "smooth" });
  if (!isTouchViewport()) return;
  let cancelled = false;
  const cancel = () => {
    cancelled = true;
  };
  const events = ["touchstart", "wheel", "keydown"] as const;
  events.forEach((e) => window.addEventListener(e, cancel, { passive: true }));
  let lastY = -1;
  let left = SETTLE_CHECKS;
  const check = () => {
    if (cancelled || left-- <= 0) {
      events.forEach((e) => window.removeEventListener(e, cancel));
      return;
    }
    const y = window.scrollY;
    if (y === lastY) snapGrid(); // smooth scroll finished → fix a stale target
    lastY = y;
    window.setTimeout(check, SETTLE_CHECK_MS);
  };
  window.setTimeout(check, SETTLE_CHECK_MS);
}

/**
 * While the Grid slide is active: if it was aligned before a viewport resize
 * (Safari bars showing / hiding), restore the alignment after the resize.
 */
export function watchGridAnchor(): () => void {
  let aligned = Math.abs(gridTop() ?? Infinity) <= ALIGNED_PX;
  const onScroll = () => {
    aligned = Math.abs(gridTop() ?? Infinity) <= ALIGNED_PX;
  };
  const onResize = () => {
    if (!aligned) return;
    requestAnimationFrame(() => {
      snapGrid();
      aligned = true;
    });
  };
  const vv = window.visualViewport;
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onResize);
  vv?.addEventListener("resize", onResize);
  return () => {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onResize);
    vv?.removeEventListener("resize", onResize);
  };
}
